import { useEffect, useRef, useState } from 'react'
import PlannerPanel from '../planner/PlannerPanel.jsx'
import PlannerSkeleton, { PlannerErrorState } from '../planner/PlannerSkeleton.jsx'
import { authenticatedFetch } from '../../utils/auth.js'
import { useToast } from '../../hooks/useToast.js'

/**
 * Last kitchen-setup step. The week starts with no dinners assigned.
 * It is cleared only on the first visit so a trip to Home does not erase progress.
 */
export default function OnboardingPlan({
  initializeEmptyWeek = false,
  onInitialized,
  onSavePrompt,
  saveAskDismissed = false,
  ...plannerProps
}) {
  const { showToast } = useToast()
  const [phase, setPhase] = useState('loading')
  const [attempt, setAttempt] = useState(0)
  const initializeRef = useRef(initializeEmptyWeek)
  const onInitializedRef = useRef(onInitialized)
  initializeRef.current = initializeEmptyWeek
  onInitializedRef.current = onInitialized

  useEffect(() => {
    let active = true

    async function prepareWeek() {
      setPhase('loading')
      const shouldInitialize = initializeRef.current
      try {
        if (shouldInitialize) {
          const cleared = await authenticatedFetch('/guest/plan', { method: 'DELETE' })
          if (!cleared.ok && cleared.status !== 404) {
            const data = await cleared.json().catch(() => ({}))
            throw new Error(data.detail || 'Could not load the week')
          }
          onInitializedRef.current?.()
        }
        if (active) {
          setPhase('ready')
          if (shouldInitialize) {
            showToast({
              title: 'Your week is ready',
              message: 'Add dinners yourself or plan with Cook.',
              duration: 2800,
            })
          }
        }
      } catch {
        if (!active) return
        setPhase('error')
      }
    }

    void prepareWeek()
    return () => {
      active = false
    }
  }, [attempt, showToast])

  if (phase === 'loading') {
    return <PlannerSkeleton onboardingMode />
  }

  if (phase === 'error') {
    return (
      <PlannerErrorState
        onboardingMode
        onRetry={() => setAttempt((value) => value + 1)}
      />
    )
  }

  return (
    <PlannerPanel
      {...plannerProps}
      guestMode
      onboardingMode
      onSavePrompt={onSavePrompt}
      saveAskDismissed={saveAskDismissed}
    />
  )
}
